import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { QueryClient } from 'react-query';
import { TaskCenterTypes, TaskCenterQueries } from '@frontend/api-task-center';
import { IconName, IconProps } from '@frontend/icons';
import { emptyStateGraphics, statusSwitcherVariantsColor } from '@frontend/design-system';

dayjs.extend(utc);
dayjs.extend(timezone);

export const getTaskEmptyState = (status?: TaskCenterTypes.TaskStatus): keyof typeof emptyStateGraphics => {
  switch (status) {
    case TaskCenterTypes.TaskStatus.STATUS_TODO:
      return 'taskTodo';
    case TaskCenterTypes.TaskStatus.STATUS_IN_PROGRESS:
      return 'taskInProgress';
    case TaskCenterTypes.TaskStatus.STATUS_COMPLETED:
      return 'taskCompleted';
    default:
      return 'taskTodo';
  }
};

export const getPriorityIcon = (priority: TaskCenterTypes.PriorityType): IconProps => {
  if (priority === TaskCenterTypes.PriorityType.PRIORITY_HIGH) {
    return { name: 'priority-high', color: 'error', size: 16 };
  } else if (priority === TaskCenterTypes.PriorityType.PRIORITY_MEDIUM) {
    return { name: 'priority-medium', size: 16 };
  } else if (priority === TaskCenterTypes.PriorityType.PRIORITY_LOW) {
    return { name: 'priority-low', size: 16 };
  }
  return { name: 'no-priority', size: 16 };
};

export const getStatusIconName = (status?: TaskCenterTypes.TaskStatus): { name: IconName; color?: string } => {
  if (status === TaskCenterTypes.TaskStatus.STATUS_COMPLETED) {
    return { name: 'check-small', color: statusSwitcherVariantsColor['success'].color };
  } else if (status === TaskCenterTypes.TaskStatus.STATUS_IN_PROGRESS) {
    return { name: 'in-progress-small', color: statusSwitcherVariantsColor['warning'].color };
  } else if (status === TaskCenterTypes.TaskStatus.STATUS_TODO) {
    return { name: 'pending-small', color: statusSwitcherVariantsColor['critical'].color };
  }
  return { name: 'pending-small', color: statusSwitcherVariantsColor['default'].color };
};

export const getLocationTimeZone = (
  locationData: { id: string; name?: string; timeZone?: string }[],
  locationId?: string
) => {
  if (!locationId) {
    return '';
  }
  const location = locationData.find((loc) => loc.id === locationId);
  return location?.timeZone;
};

export const formatDate = (format: string, date?: string) => {
  if (!date) return '';

  return dayjs(date).format(format);
};

export const convertUTCToLocal = ({ date, tz, hasTime }: { date?: string; tz?: string; hasTime?: boolean }) => {
  if (!date || !tz) return '';

  if (!hasTime) {
    return dayjs(date).tz(tz).format('YYYY-MM-DDT');
  }

  return dayjs.utc(date).tz(tz).format('YYYY-MM-DDTHH:mm:00');
};

export const invalidateWidgetTasksListQuery = (client: QueryClient, status?: TaskCenterTypes.TaskStatus) => {
  if (status && status !== TaskCenterTypes.TaskStatus.STATUS_COMPLETED) {
    client.invalidateQueries([...TaskCenterQueries.queryEndpointKeys.widgetTasksList, status]);
  }
};
